
import { mapGetters, mapActions } from 'vuex';

import * as apiPlayground from '../api/public/playground';
import * as apiGeonames from '../api/geonames/geo';

// Models
import Location from '../models/location';

import errorsMixin from '../mixins/error-handler';

export default {
  name: 'SResultListView',
  mixins: [errorsMixin],
  props: {
    showList: {
      type: Boolean,
      default: true,
    },
    contextDetail: {
      type: Object,
      default: null,
    },
    context: {
      type: Object,
      default: null,
    },
    content: {
      type: Object,
      default: null,
    },
    adminCities: {
      type: Array,
      default: null,
    },
    id: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      place: null,
      contextTitle: '',
      geoSearch: false,
      contentReady: false,
      listNearestCity: null,
      listPlaygrounds: [],
      noResult: false,
      scrollPosition: 0,
      indexOutCity: null,
      isComponentActive: false,
    };
  },
  computed: {
    ...mapGetters({
      getResults: 'transferData/getResults',
      getCurrentSearch: 'transferData/getCurrentSearch',
      getCurrentDate: 'transferData/getCurrentDate',
      getLargeSize: 'screen/getLargeSize',
      getMediumSize: 'screen/getMediumSize',
      getSmallSize: 'screen/getSmallSize',
      getXSmallSize: 'screen/getXSmallSize',
      getWindowWidth: 'screen/getWindowWidth',
    }),
  },
  created() {
    this.initList();
  },
  activated() {
    // Update the location if the list was in keep-alive and the place has changed
    if (this.contextDetail?.place && this.contextDetail?.place !== this.place) {
      const newSearchLocation = new Location(this.getCurrentSearch);
      this.setLocation(newSearchLocation);
    }
    this.isComponentActive = true;
    // Call setPlaygrounds to synchronize changes
    this.setPlaygrounds();

    if (this.$refs.scrollContainer) {
      this.hideOnScroll = false;
      this.$refs.scrollContainer.scrollToPosition(this.scrollPosition);
      setTimeout(() => {
        this.hideOnScroll = true;
      }, 50);
    }
  },
  deactivated() {
    this.isComponentActive = false;
  },
  methods: {
    ...mapActions({
      updateCurrentDate: 'transferData/updateCurrentDate',
      updateCurrentSearch: 'transferData/updateCurrentSearch',
      updateSearchResult: 'transferData/updateSearchResult',
    }),
    initList() {
      this.contentReady = false;
      if (this.contextDetail) {
        if (this.contextDetail.store) {
          this.place = this.contextDetail.city.alias;
          this.setLocation(this.contextDetail);
          this.setPlaygrounds();
        } else {
          this.place = this.contextDetail.place;
          this.getContext(this.contextDetail);
          this.getContent(this.contextDetail);
        }
      } else if (this.context?.result && this.content) {
        this.place = this.context.result.city.alias;
        this.setContext(this.context);
        this.setContent(this.content);
        this.listNearestCity = this.adminCities;
      } else {
        this.place = this.$route.params.place;
        this.getContext({ ...this.$route.params, ...this.$route.query });
        this.getContent({ ...this.$route.params, ...this.$route.query });
      }
    },
    // Called from List.vue
    updateList(data) {
      this.contentReady = false;
      this.place = data.place;
      this.getContext(data);
      this.getContent(data);
    },
    getContext(route) {
      const { lat, lon, place } = route;
      if (lat && lon) {
        apiGeonames
          .reverse({ lat, lon }, this)
          .then((res) => {
            // Use lat and lon if searching by geographic coordinates
            res.result.location.lat = lat;
            res.result.location.lon = lon;
            this.setContext({ ...res, geoSearch: true, userLocation: route.userLocation });
          })
          .catch(() => {
            if (process.client) {
              this.$newRouter.replace({
                name: '404',
                params: { url: this.$route.path },
              });
            }
          });
      } else if (place) {
        apiGeonames
          .detail({ search: place }, this)
          .then((res) => {
            this.setContext(res);
          })
          .catch(() => {
            if (process.client) {
              this.$newRouter.replace({
                name: '404',
                params: { url: this.$route.path },
              });
            }
          });
      }
    },
    setContext(context) {
      const location = new Location(context.result);
      location.geoSearch = context.geoSearch;
      location.userLocation = context.userLocation ?? false;
      this.updateCurrentSearch(location);
      this.setLocation(location);
    },
    setLocation(location) {
      this.contextTitle = location.city.name;
      // Set the context of a search by geolocation or not
      this.geoSearch = location.geoSearch;
      this.$emit('context-ready', {
        contextTitle: this.contextTitle,
        contextGeo: location.geoSearch,
      });
    },

    getContent(route) {
      const { lat, lon, place } = route;
      this.listNearestCity = null;
      const data = lat && lon
        ? { lat, lon } : { place };
      apiPlayground.list(data, this)
        .then((list) => {
          this.setContent(list);
        }).catch((error) => {
          // Validation error
          if (error.response && error.response.status === 400) {
            this.updateSearchResult([]);
            if (this.errors$.hasErrors()) {
              return;
            }
            this.errors$.handleError(true, 'playground_search', 'form');
          }
        });
    },
    setContent(content) {
      this.updateCurrentDate(content.time);
      this.updateSearchResult([...content.playgrounds]);
      this.setPlaygrounds();
    },
    async setPlaygrounds() {
      this.listPlaygrounds = this.getResults;
      this.noResult = this.listPlaygrounds.length <= 0;
      if (this.noResult && !this.listNearestCity) {
        await this.getNoResult();
      }
      this.indexOutCity = !this.geoSearch ? this.listPlaygrounds.findIndex((play) => play.city.alias !== this.place) : null;
      this.$emit('content-ready', {
        listPlaygrounds: this.listPlaygrounds,
        noResult: this.noResult,
        listNearestCity: this.listNearestCity,
      });
      this.contentReady = true;
    },
    loadDetail(playground) {
      if (this.id !== playground.id) {
        this.scrollPosition = this.$refs.scrollContainer.getPosition();
        this.$emit('load-detail', playground);
      }
    },
    getNoResult() {
      return new Promise((resolve) => {
        const input = {
          size: 3,
          lat: this.lat,
          lon: this.lon,
          place: this.place,
        };
        apiPlayground.adminCities(input, this).then((res) => {
          this.listNearestCity = res.results;
        }).catch((error) => {
          // Validation error
          if (error.response && error.response.status === 400) {
            if (this.errors$.hasErrors()) {
              return;
            }
            this.errors$.handleError(true, 'playground_search', 'form');
          }
        }).finally(() => {
          resolve();
        });
      });
    },
    canDisplayAdInList(index) {
      if (this.contextDetail || !this.isComponentActive) {
        return false;
      }

      const pos = index + 1;
      const sizes = [this.getLargeSize, this.getMediumSize, this.getSmallSize, this.getXSmallSize];
      const nbItems = [6, 5, 6, 5];

      for (let i = 0; i < sizes.length; i += 1) {
        if (this.getWindowWidth >= sizes[i]) {
          return (pos % nbItems[i] === 0);
        }
      }

      return (pos % 10 === 0);
    },
    scrollToCurrent(id) {
      if (id !== 0) {
        // - 50 for shadow offset
        const playOffsetTop = this.$refs[`listItem-${id}`][0].$el.offsetTop;
        const currentListItemPosition = playOffsetTop - 50 - this.$refs.scrollContainer.getPosition();
        this.$refs.scrollContainer.scrollToPosition(currentListItemPosition);
      }
    },
  },
};
